import React from "react";
import {
  Drawer,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { TravelCycleOutcomeGroups } from "@shared/constants/travel-cycle-causes.enum";
import { ALERT_GLOSSARY, CYCLE_GROUP_OF, CYCLE_GROUPS, CYCLE_OUTCOMES } from "../../constants";
import {
  GlossaryContainer,
  GlossaryHeader,
  GlossaryIntro,
  GlossaryGroups,
  GlossarySectionTitle,
  MetadataChips
} from "../../styles";

/** Color of the telemetry alerts card. */
const TELEMETRY_COLOR = "#597aa7";

/** Props for the AlertGlossary drawer. */
interface AlertGlossaryProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called when the user closes the drawer. */
  onClose: () => void;
}

/**
 * Right-side help drawer of the travel analysis. One card per group of failure causes, then one card with
 * every telemetry alert type. Each card opens in place and the others keep their state; inside, every
 * cause shows what it means and what to do, and every alert its trigger, scope and captured details.
 */
const AlertGlossary: React.FC<AlertGlossaryProps> = ({ open, onClose }) => {

  const { t } = useTranslation();

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <GlossaryContainer>
        <GlossaryHeader>
          <div className="glossary-title">
            <HelpOutlineIcon />
            <span>{t(TravelAnalysisMessages.glossaryDrawerTitle)}</span>
          </div>
          <IconButton size="small" onClick={onClose} aria-label={t(TravelAnalysisMessages.close) ?? ""}>
            <CloseIcon />
          </IconButton>
        </GlossaryHeader>
        <GlossaryIntro>
          <h3>{t(TravelAnalysisMessages.glossaryIntroTitle)}</h3>
          <p>{t(TravelAnalysisMessages.glossaryIntroText)}</p>
        </GlossaryIntro>

        <GlossarySectionTitle>{t(TravelAnalysisMessages.glossaryCyclesSection)}</GlossarySectionTitle>
        <GlossaryGroups>
          {CYCLE_GROUPS.map((group) => {
            const causes = CYCLE_OUTCOMES.filter((entry) => CYCLE_GROUP_OF[entry.outcome].key === group.key);

            return (
              <Accordion key={group.key} TransitionProps={{ unmountOnExit: true }} style={{ ["--gc" as string]: group.color }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <span className="group-title">
                    {t(group.nameKey)}
                    <span className="group-count">{t(TravelAnalysisMessages.glossaryCausesCount, { count: causes.length })}</span>
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="group-body">
                    {causes.map((entry) => (
                      <div key={entry.outcome} className="glossary-item">
                        <h4>{t(entry.nameKey)}</h4>
                        <p>{t(entry.descriptionKey)}</p>
                        <span className="todo">{`${t(TravelAnalysisMessages.glossaryWhatToDo)}: ${t(entry.actionKey)}.`}</span>
                      </div>
                    ))}
                    {group.key === TravelCycleOutcomeGroups.EQUIPMENT && (
                      <div className="glossary-note">{t(TravelAnalysisMessages.glossarySensorHealth)}</div>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </GlossaryGroups>

        <GlossarySectionTitle>{t(TravelAnalysisMessages.glossaryAlertsSection)}</GlossarySectionTitle>
        <GlossaryGroups>
          <Accordion TransitionProps={{ unmountOnExit: true }} style={{ ["--gc" as string]: TELEMETRY_COLOR }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <span className="group-title">
                {t(TravelAnalysisMessages.telemetryTitle)}
                <span className="group-count">{t(TravelAnalysisMessages.glossaryAlertsCount, { count: ALERT_GLOSSARY.length })}</span>
              </span>
            </AccordionSummary>
            <AccordionDetails>
              <div className="group-body">
                {ALERT_GLOSSARY.map((entry) => (
                  <div key={entry.id} className="glossary-item">
                    <h4>{t(entry.titleKey)}</h4>
                    <p>{t(entry.descriptionKey)}</p>
                    <p><strong>{`${t(TravelAnalysisMessages.glossaryWhenLabel)}: `}</strong>{t(entry.whenKey)}</p>
                    <p><strong>{`${t(TravelAnalysisMessages.glossaryAppliesLabel)}: `}</strong>{t(entry.appliesKey)}</p>
                    <MetadataChips>
                      {entry.metadataKeys.map((metaKey) => (
                        <Chip key={metaKey} size="small" label={t(metaKey)} />
                      ))}
                    </MetadataChips>
                    <span className="meta">{t(entry.metadataHintKey)}</span>
                  </div>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        </GlossaryGroups>
      </GlossaryContainer>
    </Drawer>
  );
};

export default AlertGlossary;
